import "./agentic.css";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Mission, bi, type PhaseKey } from "./mission/engine";
import { runMission } from "./mission/lead";
import { tools } from "./mission/tools";
import { bindCatalogue } from "./mission/text";
import type { MissionOptions } from "./mission/agents/assessment";
import { IntakeView } from "./view/Intake";
import { MissionView } from "./view/MissionView";
import { TopBar } from "./view/Shell";
import { LeadOrb } from "./view/LeadPanel";
import { useAgxTheme, useMission, type AgxTheme } from "./view/kit";

/**
 * Agentic review of an economic concentration, full screen: the notification and its files at intake, then the lead agent runs the
 * procedure as waves of agents working side by side on the canvas, asks the officer what only the officer decides and fires the workflow's
 * formal steps at each gate. `/concentration/agentic` starts a new notification; `/concentration/agentic/:caseId` opens a filed one.
 */
export function AgenticPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const { locale } = useLocale();
  const { t, i18n } = useTranslation();
  // The agents write with the back office's own words: stage words, band names and counted words in their plural forms.
  bindCatalogue(i18n);
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const [mission, setMission] = useState<Mission | null>(null);
  const [igniting, setIgniting] = useState(false);
  const [theme, toggleTheme] = useAgxTheme();
  const detail = useQuery({ queryKey: ["agentic", "case", caseId], enabled: !!caseId, queryFn: () => tools.caseDetail(api, caseId!) });

  useEffect(() => () => mission?.dispose(), [mission]);
  // Another file chosen while a mission is open ends that mission.
  useEffect(() => { setMission((m) => (m && m.getSnapshot().caseId !== caseId ? (m.dispose(), null) : m)); }, [caseId]);

  // One mission at a time: a second click while the team assembles must not start a second, headless mission.
  const starting = useRef(false);
  const start = async (id: string, options: MissionOptions, startAt: PhaseKey) => {
    if (starting.current) return;
    starting.current = true;
    setIgniting(true);
    const d = id === caseId && detail.data ? detail.data : await tools.caseDetail(api, id);
    const m = new Mission(api, () => localeRef.current, { caseId: id, caseNumber: d.summary.caseNumber, title: bi(d.summary.title, d.summary.titleAr ?? d.summary.title) });
    if (id !== caseId) navigate(`/concentration/agentic/${id}`, { replace: false });
    window.setTimeout(() => {
      setMission(m);
      setIgniting(false);
      starting.current = false;
      void runMission(m, options, startAt);
    }, 1400);
  };

  const exit = () => navigate(caseId ? `/cases/${caseId}` : "/concentration");
  const summary = detail.data?.summary;

  // A <main> like the staff shell's: case text on this page goes through the same content-language layer (Arabic terms for the
  // abbreviations, English passages in their translation with the original on hover).
  return (
    <main className="agx" data-theme={theme}>
      <AnimatePresence mode="wait">
        {mission ? (
          <motion.div key="mission" className="relative flex min-h-0 flex-1 flex-col" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
            <MissionScreen mission={mission} onExit={exit} theme={theme} onTheme={toggleTheme} />
          </motion.div>
        ) : (
          <motion.div key="intake" className="relative flex min-h-0 flex-1 flex-col" initial={{ opacity: 0 }} animate={{ opacity: igniting ? 0.25 : 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <TopBar caseNumber={summary?.caseNumber} title={summary ? (locale === "ar" && summary.titleAr ? summary.titleAr : summary.title) : undefined} theme={theme} onTheme={toggleTheme} onExit={exit} onOpenFile={caseId ? exit : undefined} />
            <IntakeView key={caseId ?? "new"} caseId={caseId} onAnalyse={(id, o, s) => void start(id, o, s)} onOpenCase={(id) => navigate(`/concentration/agentic/${id}`)} onNew={() => navigate("/concentration/agentic")} />
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {igniting ? (
          <motion.div className="pointer-events-none fixed inset-0 z-50 grid place-items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="flex flex-col items-center gap-5">
              <LeadOrb speaking />
              <p className="agx-shimmer text-[14px] font-medium">{t("backoffice:agentic.igniting")}</p>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </main>
  );
}

function MissionScreen({ mission, onExit, theme, onTheme }: { mission: Mission; onExit: () => void; theme: AgxTheme; onTheme: () => void }) {
  const state = useMission(mission);
  const { locale } = useLocale();
  const navigate = useNavigate();
  return (
    <>
      <TopBar
        caseNumber={state.caseNumber}
        title={locale === "ar" ? state.title.ar : state.title.en}
        state={state}
        theme={theme}
        onTheme={onTheme}
        onExit={onExit}
        onPause={() => mission.pause()}
        onPlay={() => mission.play()}
        onSpeed={(s) => mission.setSpeed(s)}
        onSelectPhase={(p) => mission.setViewPhase(p)}
        onOpenFile={() => navigate(`/cases/${state.caseId}`)}
      />
      <MissionView mission={mission} state={state} />
    </>
  );
}
